class FeaturesCarousel {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.sectionId = wrapper.dataset.sectionId;
    this.track = wrapper.querySelector('.features-cards-track');
    this.viewport = wrapper.querySelector('.features-cards-viewport');
    this.cards = Array.from(this.track.querySelectorAll('.features-card'));
    this.totalCards = this.cards.length;
    this.currentPage = 0;
    this.mobileViewportMinHeightPx = null;

    this.prevDesktop = document.getElementById(`FeaturesPrev-${this.sectionId}`);
    this.nextDesktop = document.getElementById(`FeaturesNext-${this.sectionId}`);
    this.prevMobile = document.getElementById(`FeaturesPrevMobile-${this.sectionId}`);
    this.nextMobile = document.getElementById(`FeaturesNextMobile-${this.sectionId}`);

    this.perPageDesktop = parseInt(wrapper.dataset.perPageDesktop, 10) || 4;
    this.perPageMobile = parseInt(wrapper.dataset.perPageMobile, 10) || 4;

    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);

    this.bindEvents();
    this.init();
  }

  getPerPage() {
    return window.matchMedia('(min-width: 990px)').matches
      ? this.perPageDesktop
      : this.perPageMobile;
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  getTotalPages() {
    return Math.ceil(this.totalCards / this.getPerPage());
  }

  clearMobileViewportMinHeight() {
    if (!this.viewport) return;
    this.viewport.style.minHeight = '';
    this.mobileViewportMinHeightPx = null;
  }

  lockMobileViewportMinHeight() {
    if (!this.viewport || !this.isMobileViewport()) return;
    if (this.mobileViewportMinHeightPx != null) return;

    const perPage = this.getPerPage();
    if (this.totalCards < perPage) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isMobileViewport() || this.mobileViewportMinHeightPx != null || !this.viewport) return;
        const height = Math.ceil(this.viewport.getBoundingClientRect().height);
        if (height > 0) {
          this.mobileViewportMinHeightPx = height;
          this.viewport.style.minHeight = `${height}px`;
        }
      });
    });
  }

  updateBtnState(btn, disabled) {
    if (!btn) return;
    btn.classList.toggle('is-disabled', disabled);
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  setupPages() {
    const perPage = this.getPerPage();

    this.track.querySelectorAll('.features-cards-page').forEach((page) => {
      while (page.firstChild) this.track.appendChild(page.firstChild);
      page.remove();
    });

    const allCards = Array.from(this.track.querySelectorAll('.features-card'));
    allCards.forEach((card) => {
      card.style.display = '';
    });

    for (let i = 0; i < allCards.length; i += perPage) {
      const page = document.createElement('div');
      page.className = 'features-cards-page';
      allCards.slice(i, i + perPage).forEach((card) => page.appendChild(card));
      this.track.appendChild(page);
    }

    this.track.style.transform = '';
  }

  goToPage(page) {
    const total = this.getTotalPages();
    if (page < 0 || page >= total) return;
    this.currentPage = page;

    const gapPx = 24;
    this.track.style.transform = `translateX(calc(-${this.currentPage * 100}% - ${this.currentPage * gapPx}px))`;

    const atStart = this.currentPage <= 0;
    const atEnd = this.currentPage >= total - 1;

    this.updateBtnState(this.prevDesktop, atStart);
    this.updateBtnState(this.nextDesktop, atEnd);
    this.updateBtnState(this.prevMobile, atStart);
    this.updateBtnState(this.nextMobile, atEnd);
  }

  onPrev() {
    if (this.currentPage > 0) this.goToPage(this.currentPage - 1);
  }

  onNext() {
    if (this.currentPage < this.getTotalPages() - 1) this.goToPage(this.currentPage + 1);
  }

  init() {
    this.clearMobileViewportMinHeight();
    this.currentPage = 0;
    this.setupPages();
    this.goToPage(0);
    this.lockMobileViewportMinHeight();
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      const pages = this.getTotalPages();
      if (this.currentPage >= pages) this.currentPage = Math.max(0, pages - 1);
      this.setupPages();
      this.goToPage(this.currentPage);

      if (this.isMobileViewport()) {
        if (this.mobileViewportMinHeightPx == null) this.lockMobileViewportMinHeight();
      } else {
        this.clearMobileViewportMinHeight();
      }
    }, 100);
  }

  onSectionLoad(event) {
    if (event.detail && event.detail.sectionId === this.sectionId) this.init();
  }

  bindEvents() {
    this.prevDesktop?.addEventListener('click', this.onPrev);
    this.nextDesktop?.addEventListener('click', this.onNext);
    this.prevMobile?.addEventListener('click', this.onPrev);
    this.nextMobile?.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  destroy() {
    this.prevDesktop?.removeEventListener('click', this.onPrev);
    this.nextDesktop?.removeEventListener('click', this.onNext);
    this.prevMobile?.removeEventListener('click', this.onPrev);
    this.nextMobile?.removeEventListener('click', this.onNext);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    clearTimeout(this.resizeTimer);
  }
}

function initFeaturesCarousels() {
  document.querySelectorAll('[data-features-carousel]').forEach((wrapper) => {
    if (wrapper.featuresCarousel) return;
    wrapper.featuresCarousel = new FeaturesCarousel(wrapper);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFeaturesCarousels);
} else {
  initFeaturesCarousels();
}

document.addEventListener('shopify:section:load', initFeaturesCarousels);
